import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Building2, PiggyBank, HandCoins, ScrollText, Calculator,
  Wallet, Users, Bell, Shield, FileBarChart, UserCog,
  Save, AlertCircle, Plus, Pencil, ToggleLeft, ToggleRight, Loader2,
  MapPin, Mail, Phone, Globe, DollarSign, Database, RefreshCw,
  ChevronLeft, ChevronRight, Upload, FileText, Settings as SettingsIcon,
  Scale, Gavel, CreditCard, CheckCheck,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api, PUBLIC_API_URL } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/auth.context";
import PaymentMethods from "@/pages/PaymentMethods";
import { canDownloadSystemBackup } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";

const defaultConfig: Record<string, any> = {
  organization: {
    cooperativeName: "",
    logo: "",
    registrationNumber: "",
    tin: "",
    physicalAddress: "",
    district: "",
    sector: "",
    cell: "",
    village: "",
    contactPhone: "",
    contactEmail: "",
    website: "",
    description: "",
    dateEstablished: "",
  },
  savings: {
    shareValue: 10000,
    minSharesRequired: 1,
    maxSharesAllowed: null,
    minMonthlyContribution: 10000,
    contributionGracePeriodDays: 5,
    allowExtraContributions: true,
    allowBackdatedContributions: false,
    shareCertificateNumberFormat: "SH-{year}-{number}",
    dividendCalculationMethod: "proportional",
    annualDividendRate: 8,
    dividendFrequency: "annual",
  },
  loanPolicies: {
    // Active loan rules
    allowMultipleActiveLoans: false,
    maxActiveLoansPerMember: 1,
    requireLeaderOverrideForMultipleLoans: true,
    // Outstanding balance rules
    allowLoanWithOutstandingBalance: false,
    maxOutstandingBalanceAllowed: 0,
    deductOutstandingBalanceFromEligibility: true,
    // Eligibility rules
    maxLoanMultiplier: 3,
    minimumSavingsRequired: 0,
    minimumMembershipMonths: 3,
    requireNoPenalties: false,
    allowLoanIfPenaltiesExist: true,
    // Approval rules
    requiredLoanApprovals: 3,
    requiredApprovalRoles: ["President", "Secretary", "Accountant"],
    allowAdminOverride: true,
    requireApprovalComments: false,
    // Loan limits
    globalMinimumLoanAmount: 1000,
    globalMaximumLoanAmount: 10000000,
    maxLoanTermMonths: 60,
    defaultInterestRate: 12,
    gracePeriodDays: 30,
    // Guarantor rules
    requireGuarantors: true,
    minimumGuarantors: 0,
    guarantorMinimumSavings: 0,
    allowGuarantorWithActiveLoan: false,
    // Late repayment / penalties
    latePenaltyEnabled: true,
    latePenaltyRate: 2,
    latePenaltyType: "percentage",
    gracePeriodBeforePenaltyDays: 30,
    // Legacy fields
    minMembershipPeriod: 3,
    defaultPenaltyPercentage: 2,
    overdueThresholdDays: 30,
    maxActiveLoans: 1,
    autoLoanNumberGeneration: true,
    requireApprovalWorkflow: true,
    loanApprovalLevels: 3,
    // Loan reminder settings
    enableLoanReminders: true,
    reminderDaysBeforeDue: [7, 3, 1],
    overdueReminderDays: [1, 3, 7],
    enablePenaltyWarning: true,
    enableSmsReminders: false,
    enableEmailReminders: false,
    enableWhatsappReminders: false,
    reminderMessageTemplate: "Dear {memberName}, your payment of {amountDue} FRW for loan {loanCode} is due on {dueDate}. Please make payment to avoid penalties.",
  },
  accounting: {
    currency: "RWF",
    currencySymbol: "FRW",
    financialYearStart: "",
    financialYearEnd: "",
    chartOfAccounts: "",
    interestIncomeAccount: "",
    loanReceivableAccount: "",
    savingsLiabilityAccount: "",
    penaltyIncomeAccount: "",
  },
  payroll: {
    payrollDeductionEnabled: true,
    payrollCutoffDate: 25,
    payrollExportFormat: "CSV",
    employerName: "",
    autoGeneratePayrollReports: true,
  },
  members: {
    memberNumberFormat: "M{year}{number}",
    autoGenerateMemberIds: true,
    minimumAge: 18,
    membershipFee: 5000,
    registrationFee: 2000,
    membershipApprovalWorkflow: true,
  },
  notifications: {
    smsNotifications: false,
    emailNotifications: true,
    contributionReminders: true,
    loanDueReminders: true,
    loanApprovalNotifications: true,
    paymentConfirmationNotifications: true,
    reminderDaysBeforeDue: 3,
    reminderFrequency: "daily",
  },
  security: {
    forcePasswordChange: true,
    passwordExpiryDays: 90,
    twoFactorAuthentication: false,
    sessionTimeout: 30,
    loginAttemptsLimit: 5,
  },
  reports: {
    cooperativeLogoOnReports: true,
    signatureOnReports: true,
    watermark: "",
    defaultReportFormat: "PDF",
    reportFooterText: "",
  },
};

const tabs = [
  { id: "organization", label: "Organization", icon: Building2 },
  { id: "savings", label: "Savings & Shares", icon: PiggyBank },
  { id: "loanProducts", label: "Loan Types", icon: HandCoins },
  { id: "loanPolicies", label: "Loan Policies", icon: ScrollText },
  { id: "penalties", label: "Penalties", icon: Scale },
  { id: "accounting", label: "Accounting", icon: Calculator },
  { id: "paymentMethods", label: "Payment Methods", icon: CreditCard },
  { id: "payroll", label: "Payroll", icon: Wallet },
  { id: "members", label: "Members", icon: Users },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Security", icon: Shield },
  { id: "reports", label: "Reports", icon: FileBarChart },
  { id: "users", label: "Users & Roles", icon: UserCog },
];

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("organization");
  const [config, setConfig] = useState<Record<string, any>>(() => JSON.parse(JSON.stringify(defaultConfig)));
  const [originalConfig, setOriginalConfig] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState<{ percent: number; message: string } | null>(null);
  const [restoreFormat, setRestoreFormat] = useState<"sql" | "json">("sql");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("settingsSidebarCollapsed") === "true");
  const [logoUploading, setLogoUploading] = useState(false);
  const [verifyAllLoading, setVerifyAllLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backupFileInputRef = useRef<HTMLInputElement>(null);

  const syncLogoToLocalStorage = (logoValue: string) => {
    try {
      if (logoValue) {
        localStorage.setItem("cooperativeLogo", logoValue);
      } else {
        localStorage.removeItem("cooperativeLogo");
      }
    } catch {
      // localStorage might be unavailable
    }
  };

  // Penalty settings state
  const [penaltySettings, setPenaltySettings] = useState<any>(null);
  const [penaltySettingsLoading, setPenaltySettingsLoading] = useState(false);
  const [penaltySettingsSaving, setPenaltySettingsSaving] = useState(false);

  // Loan products state
  const [loanTypes, setLoanTypes] = useState<any[]>([]);
  const [loanTypesLoading, setLoanTypesLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  const isDirty = JSON.stringify(config) !== originalConfig;

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem("settingsSidebarCollapsed", next.toString());
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/png", "image/jpg", "image/jpeg", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      toast.error("Unsupported file type. Please upload PNG, JPG, or SVG.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size exceeds 2MB limit.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setLogoUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        updateConfig("organization", "logo", dataUrl);
        syncLogoToLocalStorage(dataUrl);
        setLogoUploading(false);
        toast.success("Logo uploaded successfully");
      };
      reader.onerror = () => {
        setLogoUploading(false);
        toast.error("Failed to read file");
      };
      reader.readAsDataURL(file);
    } catch {
      setLogoUploading(false);
      toast.error("Failed to upload logo");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    loadSettings();
    loadLoanTypes();
    loadPenaltySettings();
    const hash = window.location.hash.replace("#", "");
    if (hash && tabs.some((t) => t.id === hash)) {
      setActiveTab(hash);
    }
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get("/settings");
      const data = res.data?.data || {};
      const merged = mergeDeep(JSON.parse(JSON.stringify(defaultConfig)), data);
      setConfig(merged);
      setOriginalConfig(JSON.stringify(merged));
      syncLogoToLocalStorage(merged.organization?.logo);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      console.error("Failed to load settings:", msg);
      setConfig(JSON.parse(JSON.stringify(defaultConfig)));
      setOriginalConfig(JSON.stringify(defaultConfig));
      toast.error("Failed to load settings: " + msg);
    } finally {
      setLoading(false);
    }
  };

  const loadPenaltySettings = async () => {
    setPenaltySettingsLoading(true);
    try {
      const res = await api.get("/settings/penalties");
      setPenaltySettings(res.data?.data || null);
    } catch (err: any) {
      console.error("[PenaltySettings] Load error:", err?.response?.data || err?.message || err);
      setPenaltySettings(null);
    } finally {
      setPenaltySettingsLoading(false);
    }
  };

  const loadLoanTypes = async () => {
    setLoanTypesLoading(true);
    try {
      const res = await api.get("/loan-types");
      setLoanTypes(res.data?.data || []);
    } catch {
      // ignore
    } finally {
      setLoanTypesLoading(false);
    }
  };

  const mergeDeep = (target: any, source: any) => {
    const result = { ...target };
    for (const key of Object.keys(source)) {
      if (source[key] !== null && typeof source[key] === "object" && !Array.isArray(source[key])) {
        result[key] = mergeDeep(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    return result;
  };

  const updateConfig = (section: string, key: string, value: any) => {
    setConfig((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const updateNestedConfig = (section: string, updates: Record<string, any>) => {
    setConfig((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...updates,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings", config);
      setOriginalConfig(JSON.stringify(config));
      syncLogoToLocalStorage(config.organization?.logo);
      toast.success("Settings saved successfully");
    } catch (err: any) {
      const details = err?.response?.data?.details;
      let msg = err?.response?.data?.message || err?.message || "Unable to save settings";
      if (Array.isArray(details) && details.length > 0) {
        msg = details.join(". ");
      }
      console.error("Failed to save settings:", err?.response?.data || err);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyAll = async () => {
    setVerifyAllLoading(true);
    try {
      const { data } = await api.post("/contributions/bulk/verify-all");
      toast.success(data.message || "All contributions verified");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to verify contributions");
    } finally {
      setVerifyAllLoading(false);
    }
  };

  const downloadDatabaseBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get("/system/backup/sql", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/sql" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ikimina-database-backup-${new Date().toISOString().slice(0, 10)}.sql`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Database backup downloaded");
    } catch (e: any) {
      let msg = e?.response?.data?.message || e?.message || "Backup failed";
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.message || text || msg;
        } catch { /* keep msg */ }
      }
      toast.error(typeof msg === "string" ? msg : "Backup failed");
    } finally {
      setBackupLoading(false);
    }
  };

  const downloadJsonBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get("/system/backup/json", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ikimina-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("JSON backup downloaded");
    } catch (e: any) {
      let msg = e?.response?.data?.message || e?.message || "Backup failed";
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.message || text || msg;
        } catch { /* keep msg */ }
      }
      toast.error(typeof msg === "string" ? msg : "Backup failed");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const confirmRestore = window.confirm(
      "WARNING: Restoring a backup will overwrite all current data. This action cannot be undone. Do you want to continue?"
    );
    if (!confirmRestore) {
      e.target.value = "";
      return;
    }

    setRestoreLoading(true);
    setRestoreProgress({ percent: 0, message: "Uploading backup file..." });

    try {
      const formData = new FormData();
      formData.append("backup", file);
      const endpoint = restoreFormat === "sql"
        ? "/system/backup/restore/sql"
        : "/system/backup/restore/json";

      const token = localStorage.getItem("access_token") || "";
      const response = await fetch(`${PUBLIC_API_URL}/api${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Restore failed" }));
        throw new Error(err.message || "Restore failed");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === "progress") {
              setRestoreProgress({ percent: event.percent || 0, message: event.message || "Processing..." });
            } else if (event.type === "complete") {
              setRestoreProgress({ percent: 100, message: event.message || "Restore complete" });
              toast.success(event.message || "Database restored successfully");
              setTimeout(() => setRestoreProgress(null), 3000);
            } else if (event.type === "error") {
              throw new Error(event.message || "Restore failed");
            }
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }
    } catch (e: any) {
      const msg = e?.message || "Restore failed";
      toast.error(msg);
      setRestoreProgress(null);
    } finally {
      setRestoreLoading(false);
      e.target.value = "";
    }
  };

  // ─── Loan Type CRUD ───────────────────────────────────────────

  const handleEditLoanType = (loanType: any) => {
    navigate(`/settings/loan-types/${loanType.id}/edit`);
  };

  const handleToggleLoanType = async (loanType: any) => {
    try {
      await api.put(`/loan-types/${loanType.id}`, { isActive: !loanType.isActive });
      toast.success(`Loan type ${loanType.isActive ? "deactivated" : "activated"}`);
      loadLoanTypes();
    } catch {
      toast.error("Failed to update loan type");
    }
  };

  const getTermRange = (months: number | null | undefined): string => {
    if (months == null) return "";
    if (months <= 6) return "0-6";
    if (months <= 12) return "7-12";
    if (months <= 24) return "13-24";
    return "24+";
  };

  const getAmountRange = (amount: number | null | undefined): string => {
    if (amount == null) return "";
    if (amount <= 100000) return "0-100000";
    if (amount <= 500000) return "100000-500000";
    if (amount <= 1000000) return "500000-1000000";
    if (amount <= 5000000) return "1000000-5000000";
    return "5000000+";
  };

  const formatTerm = (term: number | null | undefined): string => {
    if (term == null) return "-";
    if (term === 1) return "1 Month";
    return `${term} Months`;
  };

  const loanTypesData = useMemo(() =>
    loanTypes.map((lt: any) => ({
      ...lt,
      statusLabel: lt.isActive ? "active" : "inactive",
      termRange: getTermRange(lt.maxTermMonths),
      amountRange: getAmountRange(lt.maxAmount),
    })),
    [loanTypes]
  );

  const filteredData = useMemo(() => {
    let result = [...loanTypesData];

    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter((lt: any) =>
        lt.name?.toLowerCase().includes(q) ||
        lt.code?.toLowerCase().includes(q) ||
        lt.interestType?.toLowerCase().includes(q) ||
        (lt.isActive ? "active" : "inactive").includes(q)
      );
    }

    if (columnFilters.length > 0) {
      result = result.filter((lt: any) => {
        return columnFilters.every((filter: any) => {
          const colId = filter.id;
          const filterValues: string[] = filter.value || [];
          if (filterValues.length === 0) return true;
          const val = lt[colId];
          return filterValues.includes(val);
        });
      });
    }

    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      result.sort((a: any, b: any) => {
        if (id === "actions") return 0;
        let aVal = a[id];
        let bVal = b[id];
        if (aVal == null) aVal = "";
        if (bVal == null) bVal = "";
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        return desc ? -cmp : cmp;
      });
    }

    return result;
  }, [loanTypesData, searchText, columnFilters, sorting]);

  const pageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = useMemo(() =>
    filteredData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredData, pageIndex, pageSize]
  );

  const handleSearch = useCallback((val: string) => {
    setSearchText(val);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const facets = [
    {
      name: "statusLabel",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
    },
    {
      name: "interestType",
      title: "Interest Type",
      type: "checkbox",
      options: [
        { label: "Flat", value: "flat" },
        { label: "Reducing Balance", value: "reducing" },
      ],
    },
    {
      name: "termRange",
      title: "Term",
      type: "checkbox",
      options: [
        { label: "0-6 Months", value: "0-6" },
        { label: "7-12 Months", value: "7-12" },
        { label: "13-24 Months", value: "13-24" },
        { label: "24+ Months", value: "24+" },
      ],
    },
    {
      name: "amountRange",
      title: "Amount Range",
      type: "checkbox",
      options: [
        { label: "0 - 100k", value: "0-100000" },
        { label: "100k - 500k", value: "100000-500000" },
        { label: "500k - 1M", value: "500000-1000000" },
        { label: "1M - 5M", value: "1000000-5000000" },
        { label: "5M+", value: "5000000+" },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0A9F43]" />
      </div>
    );
  }

  const SettingsToggle = ({ section, configKey, title, description }: { section: string; configKey: string; title: string; description?: string }) => {
    const checked = config[section]?.[configKey] ?? false;
    const id = `${section}-${configKey}`;
    return (
      <div className="flex items-center justify-between py-2">
        <div>
          <Label htmlFor={id} className="text-sm font-medium text-gray-700 cursor-pointer">{title}</Label>
          {description && <p className="text-xs text-gray-400">{description}</p>}
        </div>
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={(v) => updateConfig(section, configKey, v)}
          className="h-7 w-[52px] data-[state=checked]:bg-[#16A34A] data-[state=unchecked]:bg-[#E5E7EB] data-[state=checked]:border-[#16A34A] data-[state=unchecked]:border-[#CBD5E1] hover:data-[state=checked]:bg-[#16A34A]/90 hover:data-[state=unchecked]:bg-[#D1D5DB] focus-visible:ring-[rgba(22,163,74,0.25)]"
          thumbClassName="size-[22px] bg-white shadow-md shadow-black/25 data-[state=checked]:translate-x-[26px] data-[state=unchecked]:translate-x-0"
        />
      </div>
    );
  };

  // ─── Tab Renderers ──────────────────────────────────────────────────

  const renderOrganization = () => (
    <div className="space-y-6">
      {/* Branding */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#0A9F43]" />
            <CardTitle className="text-base">Cooperative Branding</CardTitle>
          </div>
          <CardDescription>Upload your cooperative logo for reports and certificates.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <div className="relative">
              <Avatar className="w-24 h-24 border-2 border-gray-200 rounded-lg shadow-sm">
                <AvatarImage src={config.organization?.logo || "/images/ikimina-logo.jpg"} alt="Cooperative logo" />
                <AvatarFallback className="bg-[#0A9F43]/10 text-[#0A9F43] rounded-lg">
                  <Building2 className="h-12 w-12" />
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="space-y-2 flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.svg"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={logoUploading}
                onClick={() => fileInputRef.current?.click()}
                className="text-xs"
              >
                {logoUploading ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Logo</>
                )}
              </Button>
              <p className="text-xs text-gray-400">PNG, JPG or SVG. Max 2MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cooperative Identity */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#0A9F43]" />
            <CardTitle className="text-base">Cooperative Identity</CardTitle>
          </div>
          <CardDescription>Basic information about your savings cooperative.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Cooperative Name</Label>
              <Input value={config.organization?.cooperativeName || ""} onChange={(e) => updateConfig("organization", "cooperativeName", e.target.value)} placeholder="CAISSE D'ENTRAIDE IPRC KARONGI" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Date Established</Label>
              <Input type="date" value={config.organization?.dateEstablished || ""} onChange={(e) => updateConfig("organization", "dateEstablished", e.target.value)} className="h-9 text-sm" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700">Description</Label>
            <Textarea value={config.organization?.description || ""} onChange={(e) => updateConfig("organization", "description", e.target.value)} placeholder="Brief description of your cooperative..." className="min-h-[80px] text-sm" />
          </div>
        </CardContent>
      </Card>

      {/* Registration Details */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Registration Details</CardTitle>
          </div>
          <CardDescription>Official registration and tax information.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Registration Number</Label>
              <Input value={config.organization?.registrationNumber || ""} onChange={(e) => updateConfig("organization", "registrationNumber", e.target.value)} placeholder="REG-2024-001" className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Official cooperative registration number from RBC.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Tax Identification Number (TIN)</Label>
              <Input value={config.organization?.tin || ""} onChange={(e) => updateConfig("organization", "tin", e.target.value)} placeholder="TIN-123456789" className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Tax ID issued by Rwanda Revenue Authority.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact & Address */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            <CardTitle className="text-base">Contact & Address</CardTitle>
          </div>
          <CardDescription>Physical location and contact information.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5 sm:col-span-2">
              <Label className="text-xs font-medium text-gray-700">Physical Address</Label>
              <div className="relative">
                <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input value={config.organization?.physicalAddress || ""} onChange={(e) => updateConfig("organization", "physicalAddress", e.target.value)} placeholder="Street or building name" className="pl-8 h-9 text-sm" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">District</Label>
              <Input value={config.organization?.district || ""} onChange={(e) => updateConfig("organization", "district", e.target.value)} placeholder="e.g. Karongi" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Sector</Label>
              <Input value={config.organization?.sector || ""} onChange={(e) => updateConfig("organization", "sector", e.target.value)} placeholder="e.g. Rubengera" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Cell</Label>
              <Input value={config.organization?.cell || ""} onChange={(e) => updateConfig("organization", "cell", e.target.value)} placeholder="e.g. Mugonero" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Village</Label>
              <Input value={config.organization?.village || ""} onChange={(e) => updateConfig("organization", "village", e.target.value)} placeholder="e.g. Kivumu" className="h-9 text-sm" />
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Contact Phone</Label>
              <div className="relative">
                <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input value={config.organization?.contactPhone || ""} onChange={(e) => updateConfig("organization", "contactPhone", e.target.value)} placeholder="+250 793 502 827" className="pl-8 h-9 text-sm" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Contact Email</Label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input type="email" value={config.organization?.contactEmail || ""} onChange={(e) => updateConfig("organization", "contactEmail", e.target.value)} placeholder="caisse@example.com" className="pl-8 h-9 text-sm" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Website</Label>
              <div className="relative">
                <Globe className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input value={config.organization?.website || ""} onChange={(e) => updateConfig("organization", "website", e.target.value)} placeholder="https://" className="pl-8 h-9 text-sm" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verify All Contributions */}
      <Card className="border border-green-200/80 rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-green-50/50">
          <div className="flex items-center gap-2">
            <CheckCheck className="h-5 w-5 text-green-700" />
            <CardTitle className="text-base">Verify All Contributions</CardTitle>
          </div>
          <CardDescription>Bulk verify all unverified contributions. Use this when importing historical data.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <Button type="button" variant="secondary" disabled={verifyAllLoading} onClick={handleVerifyAll} size="sm">
            {verifyAllLoading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Verifying...</>
            ) : (
              <><CheckCheck className="h-4 w-4 mr-1" /> Verify All Unverified Contributions</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* System Backup */}
      <Card className="border border-amber-200/80 rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-amber-50/50">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-amber-700" />
            <CardTitle className="text-base">System Data Backup</CardTitle>
          </div>
          <CardDescription>Download a backup or restore from a previous backup file.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {canDownloadSystemBackup(user) ? (
            <>
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Download Backup</p>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" disabled={backupLoading} onClick={downloadDatabaseBackup} size="sm">
                    {backupLoading ? "Preparing..." : "Download SQL Backup"}
                  </Button>
                  <Button type="button" variant="secondary" disabled={backupLoading} onClick={downloadJsonBackup} size="sm">
                    {backupLoading ? "Preparing..." : "Download JSON Backup"}
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Restore from Backup</p>
                <div className="flex items-center gap-2">
                  <Select value={restoreFormat} onValueChange={(v: "sql" | "json") => setRestoreFormat(v)}>
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sql">SQL File</SelectItem>
                      <SelectItem value="json">JSON File</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={restoreLoading}
                    onClick={() => backupFileInputRef.current?.click()}
                  >
                    {restoreLoading ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Restoring...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-1" /> Upload & Restore</>
                    )}
                  </Button>
                  <input
                    ref={backupFileInputRef}
                    type="file"
                    accept={restoreFormat === "sql" ? ".sql" : ".json"}
                    className="hidden"
                    onChange={handleRestoreFile}
                  />
                </div>
                <p className="text-xs text-amber-600 mt-1">Warning: Restoring overwrites all current data.</p>
              </div>

              {restoreProgress && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-blue-800">{restoreProgress.message}</span>
                    <span className="text-blue-600">{restoreProgress.percent}%</span>
                  </div>
                  <div className="w-full bg-blue-100 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${restoreProgress.percent}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-500">Contact your system administrator for database backups.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderSavings = () => (
    <div className="space-y-5">
      {/* Share Configuration */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <PiggyBank className="h-5 w-5 text-[#0A9F43]" />
            <CardTitle className="text-base">Share Configuration</CardTitle>
          </div>
          <CardDescription>Set share value, limits, and minimum contribution amounts.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Share Value (FRW)</Label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input type="number" value={config.savings?.shareValue ?? 10000} onChange={(e) => updateConfig("savings", "shareValue", Number(e.target.value))} className="pl-8 h-9 text-sm" />
              </div>
              <p className="text-xs text-gray-400">Monetary value of a single share.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Minimum Monthly Contribution (FRW)</Label>
              <Input type="number" value={config.savings?.minMonthlyContribution ?? 10000} onChange={(e) => updateConfig("savings", "minMonthlyContribution", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Minimum Shares Required</Label>
              <Input type="number" value={config.savings?.minSharesRequired ?? 1} onChange={(e) => updateConfig("savings", "minSharesRequired", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Minimum shares a member must hold.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Maximum Shares Allowed</Label>
              <Input type="number" value={config.savings?.maxSharesAllowed ?? ""} onChange={(e) => updateConfig("savings", "maxSharesAllowed", e.target.value ? Number(e.target.value) : null)} className="h-9 text-sm" placeholder="Unlimited" />
              <p className="text-xs text-gray-400">Leave empty for unlimited.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contribution Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Contribution Rules</CardTitle>
          </div>
          <CardDescription>Configure contribution deadlines, grace periods, and flexibilities.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Contribution Grace Period (Days)</Label>
              <Input type="number" value={config.savings?.contributionGracePeriodDays ?? 5} onChange={(e) => updateConfig("savings", "contributionGracePeriodDays", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Share Certificate Number Format</Label>
              <Input value={config.savings?.shareCertificateNumberFormat || ""} onChange={(e) => updateConfig("savings", "shareCertificateNumberFormat", e.target.value)} placeholder="SH-{year}-{number}" className="h-9 text-sm" />
            </div>
          </div>
          <Separator />
          <div className="space-y-1">
            <SettingsToggle section="savings" configKey="allowExtraContributions" title="Allow Extra Contributions" description="Members can contribute above the minimum required amount." />
            <SettingsToggle section="savings" configKey="allowBackdatedContributions" title="Allow Backdated Contributions" description="Allow members to contribute for past months." />
          </div>
        </CardContent>
      </Card>

      {/* Dividend Configuration */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-base">Dividend Configuration</CardTitle>
          </div>
          <CardDescription>Configure how dividends are calculated and distributed.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Calculation Method</Label>
              <Select value={config.savings?.dividendCalculationMethod || "proportional"} onValueChange={(v) => updateConfig("savings", "dividendCalculationMethod", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="proportional">Proportional to Shares</SelectItem>
                  <SelectItem value="equal">Equal Distribution</SelectItem>
                  <SelectItem value="tiered">Tiered Based on Shares</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Annual Rate (%)</Label>
              <Input type="number" value={config.savings?.annualDividendRate ?? 8} onChange={(e) => updateConfig("savings", "annualDividendRate", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Dividend Frequency</Label>
              <Select value={config.savings?.dividendFrequency || "annual"} onValueChange={(v) => updateConfig("savings", "dividendFrequency", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const loanTypesColumns: ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Loan Type" />,
      cell: ({ row }) => <div className="font-medium whitespace-nowrap">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "code",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Code" />,
      cell: ({ row }) => <div className="text-gray-600 whitespace-nowrap">{row.getValue("code") || "-"}</div>,
    },
    {
      accessorKey: "interestRate",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Interest Rate" />,
      cell: ({ row }) => {
        const rate = row.getValue("interestRate");
        return <div className="whitespace-nowrap">{rate != null && rate !== "" ? `${rate}%` : "-"}</div>;
      },
    },
    {
      accessorKey: "interestType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Interest Type" />,
      cell: ({ row }) => {
        const type = row.getValue("interestType");
        return <div className="whitespace-nowrap capitalize">{String(type) === "reducing" ? "Reducing Balance" : String(type || "Flat")}</div>;
      },
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "minAmount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Min Amount" />,
      cell: ({ row }) => {
        const val = row.getValue("minAmount");
        return <div className="whitespace-nowrap">{val != null && val !== "" ? `${Number(val).toLocaleString()} FRW` : "-"}</div>;
      },
    },
    {
      accessorKey: "maxAmount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Max Amount" />,
      cell: ({ row }) => {
        const val = row.getValue("maxAmount");
        return <div className="whitespace-nowrap">{val != null && val !== "" ? `${Number(val).toLocaleString()} FRW` : "-"}</div>;
      },
    },
    {
      accessorKey: "maxTermMonths",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Max Term" />,
      cell: ({ row }) => <div className="whitespace-nowrap">{formatTerm(row.getValue("maxTermMonths"))}</div>,
    },
    {
      accessorKey: "processingFee",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Processing Fee" />,
      cell: ({ row }) => {
        const fee = row.getValue("processingFee");
        return <div className="whitespace-nowrap">{fee != null && fee !== "" ? `${Number(fee).toLocaleString()} FRW` : "-"}</div>;
      },
    },
    {
      accessorKey: "requiredGuarantors",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Required Guarantors" />,
      cell: ({ row }) => <div className="whitespace-nowrap">{row.getValue("requiredGuarantors") ?? "-"}</div>,
    },
    {
      accessorKey: "statusLabel",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const isActive = row.original.isActive;
        return (
          <Badge variant={isActive ? "default" : "secondary"} className={isActive ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-600"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
      filterFn: (row, id, value) => value.includes(row.original.isActive ? "active" : "inactive"),
    },
    {
      id: "actions",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Actions" />,
      cell: ({ row }) => {
        const lt = row.original;
        return (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditLoanType(lt)} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleLoanType(lt)} title={lt.isActive ? "Deactivate" : "Activate"}>
              {lt.isActive ? <ToggleRight className="h-3.5 w-3.5 text-green-600" /> : <ToggleLeft className="h-3.5 w-3.5 text-gray-400" />}
            </Button>
          </div>
        );
      },
      enableSorting: false,
    },
  ];



  const handleSavePenaltySettings = async () => {
    if (!penaltySettings) return;
    setPenaltySettingsSaving(true);
    try {
      await api.put("/settings/penalties", penaltySettings);
      toast.success("Penalty settings saved successfully");
    } catch (err: any) {
      console.error("[PenaltySettings] Save error:", err?.response?.data || err?.message || err);
      toast.error(err?.response?.data?.message || "Failed to save penalty settings");
    } finally {
      setPenaltySettingsSaving(false);
    }
  };

  const updatePenaltySetting = (key: string, value: any) => {
    setPenaltySettings((prev: any) => ({ ...prev, [key]: value }));
  };

  const renderPenalties = () => {
    if (penaltySettingsLoading) {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader2 className="h-8 w-8 animate-spin text-[#0A9F43]" />
        </div>
      );
    }

    if (!penaltySettings) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>Unable to load penalty settings. Please try again.</p>
          <Button size="sm" variant="outline" className="mt-2" onClick={loadPenaltySettings}>Retry</Button>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {/* Loan Late Repayment Penalty */}
        <Card className="border rounded-md shadow-none">
          <CardHeader className="pb-3 border-b bg-gray-50/50">
            <div className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-red-600" />
              <CardTitle className="text-base">Loan Late Repayment Penalty</CardTitle>
            </div>
            <CardDescription>Configure penalties for late loan repayments.</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="loanPenaltyEnabled" className="text-sm font-medium text-gray-700 cursor-pointer">Enable Loan Repayment Penalty</Label>
                <p className="text-xs text-gray-400">Apply penalties when loan repayments are overdue.</p>
              </div>
              <Switch
                id="loanPenaltyEnabled"
                checked={penaltySettings?.loanPenaltyEnabled ?? false}
                onCheckedChange={(v) => updatePenaltySetting("loanPenaltyEnabled", v)}
                className="h-7 w-[52px] data-[state=checked]:bg-[#16A34A]"
                thumbClassName="size-[22px] bg-white shadow-md shadow-black/25 data-[state=checked]:translate-x-[26px] data-[state=unchecked]:translate-x-0"
              />
            </div>

            {penaltySettings.loanPenaltyEnabled && (
              <>
                <Separator />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Penalty Type</Label>
                    <Select
                      value={penaltySettings.loanPenaltyType || "fixed"}
                      onValueChange={(v) => updatePenaltySetting("loanPenaltyType", v)}
                    >
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Amount (FRW)</SelectItem>
                        <SelectItem value="percentage">Percentage of Overdue (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">
                      {penaltySettings.loanPenaltyType === "percentage" ? "Penalty Percentage (%)" : "Penalty Amount (FRW)"}
                    </Label>
                    <Input
                      type="number"
                      value={penaltySettings.loanPenaltyValue || 0}
                      onChange={(e) => updatePenaltySetting("loanPenaltyValue", Number(e.target.value))}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Grace Period (Days)</Label>
                    <Input
                      type="number"
                      value={penaltySettings.loanGracePeriodDays || 0}
                      onChange={(e) => updatePenaltySetting("loanGracePeriodDays", Number(e.target.value))}
                      className="h-9 text-sm"
                    />
                    <p className="text-xs text-gray-400">Days after due date before penalty applies.</p>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Apply Frequency</Label>
                    <Select
                      value={penaltySettings.loanApplyFrequency || "once"}
                      onValueChange={(v) => updatePenaltySetting("loanApplyFrequency", v)}
                    >
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">Once Only</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400">How often to apply the penalty for continued overdue.</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Contribution Late Payment Penalty */}
        <Card className="border rounded-md shadow-none">
          <CardHeader className="pb-3 border-b bg-gray-50/50">
            <div className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-base">Contribution Late Payment Penalty</CardTitle>
            </div>
            <CardDescription>Configure penalties for late or missed monthly contributions.</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="contributionPenaltyEnabled" className="text-sm font-medium text-gray-700 cursor-pointer">Enable Contribution Late Penalty</Label>
                <p className="text-xs text-gray-400">Apply penalties when members miss the monthly contribution deadline.</p>
              </div>
              <Switch
                id="contributionPenaltyEnabled"
                checked={penaltySettings?.contributionPenaltyEnabled ?? false}
                onCheckedChange={(v) => updatePenaltySetting("contributionPenaltyEnabled", v)}
                className="h-7 w-[52px] data-[state=checked]:bg-[#16A34A]"
                thumbClassName="size-[22px] bg-white shadow-md shadow-black/25 data-[state=checked]:translate-x-[26px] data-[state=unchecked]:translate-x-0"
              />
            </div>

            {penaltySettings.contributionPenaltyEnabled && (
              <>
                <Separator />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Penalty Type</Label>
                    <Select
                      value={penaltySettings.contributionPenaltyType || "fixed"}
                      onValueChange={(v) => updatePenaltySetting("contributionPenaltyType", v)}
                    >
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Amount (FRW)</SelectItem>
                        <SelectItem value="percentage">Percentage of Required (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">
                      {penaltySettings.contributionPenaltyType === "percentage" ? "Penalty Percentage (%)" : "Penalty Amount (FRW)"}
                    </Label>
                    <Input
                      type="number"
                      value={penaltySettings.contributionPenaltyValue || 0}
                      onChange={(e) => updatePenaltySetting("contributionPenaltyValue", Number(e.target.value))}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Due Day of Month</Label>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      value={penaltySettings.contributionDueDay || 1}
                      onChange={(e) => updatePenaltySetting("contributionDueDay", Number(e.target.value))}
                      className="h-9 text-sm"
                    />
                    <p className="text-xs text-gray-400">Day of month when contribution is due (1-31).</p>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Grace Period (Days)</Label>
                    <Input
                      type="number"
                      value={penaltySettings.contributionGracePeriodDays || 0}
                      onChange={(e) => updatePenaltySetting("contributionGracePeriodDays", Number(e.target.value))}
                      className="h-9 text-sm"
                    />
                    <p className="text-xs text-gray-400">Days after due date before penalty applies.</p>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Apply Frequency</Label>
                    <Select
                      value={penaltySettings.contributionApplyFrequency || "once"}
                      onValueChange={(v) => updatePenaltySetting("contributionApplyFrequency", v)}
                    >
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">Once Only</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400">How often to apply the penalty for continued non-payment.</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleSavePenaltySettings}
            disabled={penaltySettingsSaving}
            className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white"
          >
            {penaltySettingsSaving ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...</>
            ) : (
              <><Save className="h-3.5 w-3.5 mr-1.5" /> Save Penalty Settings</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderLoanProducts = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Loan Types</h3>
          <p className="text-sm text-gray-500 mt-0.5">Manage loan types offered to members.</p>
        </div>
        <Button size="sm" onClick={() => navigate("/settings/loan-types/new")} className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white">
          <Plus className="h-4 w-4 mr-1.5" />
          Add Loan Type
        </Button>
      </div>

      <DataTable
        columns={loanTypesColumns}
        data={paginatedData}
        facets={facets}
        isLoading={loanTypesLoading}
        defaultColumnVisibility={{}}
        onSearch={handleSearch}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        sorting={sorting}
        setSorting={setSorting}
        setPagination={setPagination}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={pageCount}
        isFetching={false}
      />
    </div>
  );

  const renderLoanPolicies = () => (
    <div className="space-y-5">
      {/* 1. Active Loan Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Active Loan Rules</CardTitle>
          </div>
          <CardDescription>Control whether members can have multiple active loans simultaneously.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <SettingsToggle section="loanPolicies" configKey="allowMultipleActiveLoans" title="Allow Multiple Active Loans" description="When enabled, members can hold more than one active loan at the same time." />
            {(config.loanPolicies as any)?.allowMultipleActiveLoans && (
              <div className="grid gap-1.5 ml-6">
                <Label className="text-xs font-medium text-gray-700">Maximum Active Loans Per Member</Label>
                <Input type="number" min={1} max={10} value={(config.loanPolicies as any)?.maxActiveLoansPerMember ?? 1} onChange={(e) => updateConfig("loanPolicies", "maxActiveLoansPerMember", Number(e.target.value))} className="h-9 text-sm w-32" />
                <p className="text-xs text-gray-400">Maximum number of active loans a member can hold at once.</p>
              </div>
            )}
            <SettingsToggle section="loanPolicies" configKey="requireLeaderOverrideForMultipleLoans" title="Require Leader Override" description="Admin must have the Force flag to approve a second active loan." />
          </div>
        </CardContent>
      </Card>

      {/* 2. Outstanding Balance Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <CardTitle className="text-base">Outstanding Balance Rules</CardTitle>
          </div>
          <CardDescription>Configure how existing loan balances affect new loan applications.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <SettingsToggle section="loanPolicies" configKey="allowLoanWithOutstandingBalance" title="Allow Loans With Outstanding Balance" description="When disabled, members with any outstanding loan balance cannot apply for a new loan. When enabled, a warning is shown." />
            {(config.loanPolicies as any)?.allowLoanWithOutstandingBalance && (
              <div className="grid gap-1.5 ml-6">
                <Label className="text-xs font-medium text-gray-700">Maximum Outstanding Balance Allowed (FRW)</Label>
                <Input type="number" min={0} value={(config.loanPolicies as any)?.maxOutstandingBalanceAllowed ?? 0} onChange={(e) => updateConfig("loanPolicies", "maxOutstandingBalanceAllowed", Number(e.target.value))} className="h-9 text-sm w-48" />
                <p className="text-xs text-gray-400">Set to 0 for no limit. If greater than 0, applications exceeding this threshold are blocked.</p>
              </div>
            )}
            <SettingsToggle section="loanPolicies" configKey="deductOutstandingBalanceFromEligibility" title="Deduct Outstanding Balance From Eligibility" description="When enabled, the maximum loan amount is calculated as (Savings - Outstanding Loan Balance) × Multiplier." />
          </div>
        </CardContent>
      </Card>

      {/* 3. Eligibility Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-base">Eligibility Rules</CardTitle>
          </div>
          <CardDescription>Define member eligibility criteria for loan access.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Maximum Loan Multiplier</Label>
              <Input type="number" min={1} max={10} step={0.5} value={(config.loanPolicies as any)?.maxLoanMultiplier ?? 3} onChange={(e) => updateConfig("loanPolicies", "maxLoanMultiplier", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Loan amount as a multiple of member eligible savings.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Minimum Savings Required (FRW)</Label>
              <Input type="number" min={0} value={(config.loanPolicies as any)?.minimumSavingsRequired ?? 0} onChange={(e) => updateConfig("loanPolicies", "minimumSavingsRequired", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Minimum savings balance required before a member can apply. 0 = no minimum.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Minimum Membership Months</Label>
              <Input type="number" min={0} max={24} value={(config.loanPolicies as any)?.minimumMembershipMonths ?? 3} onChange={(e) => updateConfig("loanPolicies", "minimumMembershipMonths", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Months a member must be registered before applying.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Maximum Active Loans Per Member</Label>
              <Input type="number" min={1} max={10} value={(config.loanPolicies as any)?.maxActiveLoansPerMember ?? 1} onChange={(e) => updateConfig("loanPolicies", "maxActiveLoansPerMember", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">How many active loans a member may have at once.</p>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="space-y-1">
            <SettingsToggle section="loanPolicies" configKey="requireNoPenalties" title="Require No Outstanding Penalties" description="Members with active penalties cannot apply for a loan." />
            <SettingsToggle section="loanPolicies" configKey="allowLoanIfPenaltiesExist" title="Allow Loan If Penalties Exist" description="When enabled, members can apply even with pending penalties (warning shown)." />
          </div>
        </CardContent>
      </Card>

      {/* 4. Approval Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-base">Approval Rules</CardTitle>
          </div>
          <CardDescription>Configure the loan approval workflow and required roles.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Required Loan Approvals</Label>
              <Input type="number" min={1} max={5} value={(config.loanPolicies as any)?.requiredLoanApprovals ?? 3} onChange={(e) => updateConfig("loanPolicies", "requiredLoanApprovals", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Number of leader approvals needed before a loan is approved.</p>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700 mb-2">Required Approval Roles</Label>
            <div className="flex flex-wrap gap-3">
              {["President", "Secretary", "Accountant", "Vice President"].map((role) => {
                const roles = (config.loanPolicies as any)?.requiredApprovalRoles || ["President", "Secretary", "Accountant"];
                const isChecked = roles.includes(role);
                return (
                  <label key={role} className="flex items-center gap-1.5 text-xs font-medium text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={isChecked}
                      onChange={() => {
                        const current = [...roles];
                        if (isChecked) {
                          const idx = current.indexOf(role);
                          if (idx !== -1) current.splice(idx, 1);
                        } else {
                          current.push(role);
                        }
                        updateConfig("loanPolicies", "requiredApprovalRoles", current);
                      }}
                    />
                    {role}
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">Select which roles are required to approve a loan application.</p>
          </div>
          <Separator className="my-3" />
          <div className="space-y-1">
            <SettingsToggle section="loanPolicies" configKey="allowAdminOverride" title="Allow Admin Override" description="Admins can force-approve a loan even if required approvals are missing." />
            <SettingsToggle section="loanPolicies" configKey="requireApprovalComments" title="Require Approval Comments" description="Leaders must add a comment when approving or rejecting a loan." />
          </div>
        </CardContent>
      </Card>

      {/* 5. Loan Limits */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#0A9F43]" />
            <CardTitle className="text-base">Loan Limits</CardTitle>
          </div>
          <CardDescription>Set global minimum and maximum loan amounts and terms.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Global Minimum Loan Amount (FRW)</Label>
              <Input type="number" min={0} value={(config.loanPolicies as any)?.globalMinimumLoanAmount ?? 1000} onChange={(e) => updateConfig("loanPolicies", "globalMinimumLoanAmount", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Minimum loan amount across all loan types. 0 = no global minimum.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Global Maximum Loan Amount (FRW)</Label>
              <Input type="number" min={0} value={(config.loanPolicies as any)?.globalMaximumLoanAmount ?? 10000000} onChange={(e) => updateConfig("loanPolicies", "globalMaximumLoanAmount", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Maximum loan amount across all loan types. 0 = no global maximum.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Maximum Loan Term (Months)</Label>
              <Input type="number" min={1} max={120} value={(config.loanPolicies as any)?.maxLoanTermMonths ?? 60} onChange={(e) => updateConfig("loanPolicies", "maxLoanTermMonths", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Maximum repayment period in months.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Default Interest Rate (% per year)</Label>
              <Input type="number" min={0} max={50} step={0.5} value={(config.loanPolicies as any)?.defaultInterestRate ?? 12} onChange={(e) => updateConfig("loanPolicies", "defaultInterestRate", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Annual interest rate applied to loans.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Grace Period (Days)</Label>
              <Input type="number" min={0} max={90} value={(config.loanPolicies as any)?.gracePeriodDays ?? 30} onChange={(e) => updateConfig("loanPolicies", "gracePeriodDays", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Days after disbursement before repayment begins.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 6. Guarantor Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-base">Guarantor Rules</CardTitle>
          </div>
          <CardDescription>Configure guarantor requirements for loan applications.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <SettingsToggle section="loanPolicies" configKey="requireGuarantors" title="Require Guarantors" description="Loan applications must have guarantors for approval." />
            {(config.loanPolicies as any)?.requireGuarantors && (
              <>
                <div className="grid gap-1.5 ml-6">
                  <Label className="text-xs font-medium text-gray-700">Minimum Number of Guarantors</Label>
                  <Input type="number" min={1} max={5} value={(config.loanPolicies as any)?.minimumGuarantors ?? 2} onChange={(e) => updateConfig("loanPolicies", "minimumGuarantors", Number(e.target.value))} className="h-9 text-sm w-32" />
                  <p className="text-xs text-gray-400">Minimum number of guarantors required per loan.</p>
                </div>
                <div className="grid gap-1.5 ml-6">
                  <Label className="text-xs font-medium text-gray-700">Guarantor Minimum Savings (FRW)</Label>
                  <Input type="number" min={0} value={(config.loanPolicies as any)?.guarantorMinimumSavings ?? 0} onChange={(e) => updateConfig("loanPolicies", "guarantorMinimumSavings", Number(e.target.value))} className="h-9 text-sm w-48" />
                  <p className="text-xs text-gray-400">Minimum savings balance required to serve as a guarantor. 0 = no minimum.</p>
                </div>
                <SettingsToggle section="loanPolicies" configKey="allowGuarantorWithActiveLoan" title="Allow Guarantor With Active Loan" description="Members with active loans can still serve as guarantors." />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 7. Late Repayment / Penalties */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Gavel className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base">Late Repayment & Penalties</CardTitle>
          </div>
          <CardDescription>Configure penalties for late or missed loan repayments.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <SettingsToggle section="loanPolicies" configKey="latePenaltyEnabled" title="Enable Late Repayment Penalties" description="Automatically apply penalties when repayments are overdue." />
            {(config.loanPolicies as any)?.latePenaltyEnabled && (
              <>
                <div className="grid gap-4 sm:grid-cols-2 ml-6">
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Penalty Rate</Label>
                    <Input type="number" min={0} max={50} step={0.5} value={(config.loanPolicies as any)?.latePenaltyRate ?? 2} onChange={(e) => updateConfig("loanPolicies", "latePenaltyRate", Number(e.target.value))} className="h-9 text-sm" />
                    <p className="text-xs text-gray-400">Penalty rate applied (percentage or fixed amount).</p>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-xs font-medium text-gray-700">Penalty Type</Label>
                    <Select value={(config.loanPolicies as any)?.latePenaltyType || "percentage"} onValueChange={(v) => updateConfig("loanPolicies", "latePenaltyType", v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (% of outstanding)</SelectItem>
                        <SelectItem value="fixed">Fixed Amount (FRW)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400">Whether penalty is a percentage of outstanding balance or a fixed FRW amount.</p>
                  </div>
                </div>
                <div className="grid gap-1.5 ml-6">
                  <Label className="text-xs font-medium text-gray-700">Grace Period Before Penalty (Days)</Label>
                  <Input type="number" min={0} max={90} value={(config.loanPolicies as any)?.gracePeriodBeforePenaltyDays ?? 30} onChange={(e) => updateConfig("loanPolicies", "gracePeriodBeforePenaltyDays", Number(e.target.value))} className="h-9 text-sm w-32" />
                  <p className="text-xs text-gray-400">Days after the due date before penalties begin to accrue.</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loan Reminder Settings */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#0A9F43]" />
            <CardTitle className="text-base">Loan Payment Reminders</CardTitle>
          </div>
          <CardDescription>Configure automatic payment reminders for members.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4">
            <SettingsToggle section="loanPolicies" configKey="enableLoanReminders" title="Enable Loan Reminders" description="Automatically scan active loans and create payment reminders." />
            <SettingsToggle section="loanPolicies" configKey="enablePenaltyWarning" title="Enable Penalty Warnings" description="Include penalty warning messages in reminders for overdue payments." />
            <SettingsToggle section="loanPolicies" configKey="enableSmsReminders" title="Enable SMS Reminders" description="Send reminders via SMS (requires SMS provider configuration)." />
            <SettingsToggle section="loanPolicies" configKey="enableEmailReminders" title="Enable Email Reminders" description="Send reminders via email (requires email provider configuration)." />
            <SettingsToggle section="loanPolicies" configKey="enableWhatsappReminders" title="Enable WhatsApp Reminders" description="Send reminders via WhatsApp (requires WhatsApp provider configuration)." />
            <div className="grid gap-1.5 ml-6">
              <Label className="text-xs font-medium text-gray-700">Reminder Message Template</Label>
              <textarea
                value={(config.loanPolicies as any)?.reminderMessageTemplate ?? ""}
                onChange={(e) => updateConfig("loanPolicies", "reminderMessageTemplate", e.target.value)}
                className="h-20 text-sm w-full border rounded-md p-2"
                placeholder="Dear {memberName}, your payment of {amountDue} FRW for loan {loanCode} is due on {dueDate}."
              />
              <p className="text-xs text-gray-400">Use variables: {"{memberName}"}, {"{amountDue}"}, {"{loanCode}"}, {"{dueDate}"}, {"{daysOverdue}"}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAccounting = () => (
    <div className="space-y-5">
      {/* Financial Settings */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#0A9F43]" />
            <CardTitle className="text-base">Financial Settings</CardTitle>
          </div>
          <CardDescription>Configure currency and fiscal year settings.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Currency</Label>
              <Select value={config.accounting?.currency || "RWF"} onValueChange={(v) => updateConfig("accounting", "currency", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["RWF", "USD", "EUR", "KES", "UGX", "TZS"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Currency Symbol</Label>
              <Input value={config.accounting?.currencySymbol || "FRW"} onChange={(e) => updateConfig("accounting", "currencySymbol", e.target.value)} placeholder="FRW" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Financial Year Start</Label>
              <Input type="date" value={config.accounting?.financialYearStart || ""} onChange={(e) => updateConfig("accounting", "financialYearStart", e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Financial Year End</Label>
              <Input type="date" value={config.accounting?.financialYearEnd || ""} onChange={(e) => updateConfig("accounting", "financialYearEnd", e.target.value)} className="h-9 text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Mapping */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Accounts Mapping</CardTitle>
          </div>
          <CardDescription>Map general ledger accounts for automated booking.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Savings Liability Account</Label>
              <Input value={config.accounting?.savingsLiabilityAccount || ""} onChange={(e) => updateConfig("accounting", "savingsLiabilityAccount", e.target.value)} placeholder="GL-201000" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Loan Receivable Account</Label>
              <Input value={config.accounting?.loanReceivableAccount || ""} onChange={(e) => updateConfig("accounting", "loanReceivableAccount", e.target.value)} placeholder="GL-110000" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Interest Income Account</Label>
              <Input value={config.accounting?.interestIncomeAccount || ""} onChange={(e) => updateConfig("accounting", "interestIncomeAccount", e.target.value)} placeholder="GL-400000" className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Penalty Income Account</Label>
              <Input value={config.accounting?.penaltyIncomeAccount || ""} onChange={(e) => updateConfig("accounting", "penaltyIncomeAccount", e.target.value)} placeholder="GL-401000" className="h-9 text-sm" />
            </div>
          </div>
          <Separator className="my-4" />
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700">Chart of Accounts</Label>
            <Textarea value={config.accounting?.chartOfAccounts || ""} onChange={(e) => updateConfig("accounting", "chartOfAccounts", e.target.value)} placeholder="Define the chart of accounts structure..." className="min-h-[60px] text-sm" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderPayroll = () => (
    <div className="space-y-5">
      {/* Payroll Configuration */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-base">Payroll Configuration</CardTitle>
          </div>
          <CardDescription>Configure payroll integration settings for salary-based contributions.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-1">
            <SettingsToggle section="payroll" configKey="payrollDeductionEnabled" title="Payroll Deduction Enabled" description="Allow automated payroll deduction for member contributions." />
            <SettingsToggle section="payroll" configKey="autoGeneratePayrollReports" title="Auto Generate Payroll Reports" description="Automatically generate reports for each payroll period." />
          </div>
          <Separator className="my-3" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Payroll Cutoff Date (Day of Month)</Label>
              <Input type="number" value={config.payroll?.payrollCutoffDate ?? 25} onChange={(e) => updateConfig("payroll", "payrollCutoffDate", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Employer Name</Label>
              <Input value={config.payroll?.employerName || ""} onChange={(e) => updateConfig("payroll", "employerName", e.target.value)} placeholder="IPRC Karongi" className="h-9 text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payroll Export */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base">Payroll Export</CardTitle>
          </div>
          <CardDescription>Configure payroll export format and templates.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Default Export Format</Label>
              <Select value={config.payroll?.payrollExportFormat || "CSV"} onValueChange={(v) => updateConfig("payroll", "payrollExportFormat", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["CSV", "Excel", "PDF", "XML"].map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Export Template</Label>
              <Input value={config.payroll?.payrollExportTemplate || ""} onChange={(e) => updateConfig("payroll", "payrollExportTemplate", e.target.value)} placeholder="Default Template" className="h-9 text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderMembers = () => (
    <div className="space-y-5">
      {/* Membership Rules */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-sky-600" />
            <CardTitle className="text-base">Membership Rules</CardTitle>
          </div>
          <CardDescription>Configure member registration requirements.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Registration Fee (FRW)</Label>
              <Input type="number" value={config.members?.registrationFee ?? 2000} onChange={(e) => updateConfig("members", "registrationFee", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Membership Fee (FRW)</Label>
              <Input type="number" value={config.members?.membershipFee ?? 5000} onChange={(e) => updateConfig("members", "membershipFee", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Minimum Age</Label>
              <Input type="number" value={config.members?.minimumAge ?? 18} onChange={(e) => updateConfig("members", "minimumAge", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Minimum age required for membership.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Member Numbering */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-base">Member Numbering</CardTitle>
          </div>
          <CardDescription>Configure member ID generation rules.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-1">
            <SettingsToggle section="members" configKey="autoGenerateMemberIds" title="Auto Generate Member IDs" description="Automatically assign unique member IDs on registration." />
            <SettingsToggle section="members" configKey="membershipApprovalWorkflow" title="Membership Approval Workflow" description="Require approval for new member registrations." />
          </div>
          <Separator className="my-3" />
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700">Member Number Format</Label>
            <Input value={config.members?.memberNumberFormat || ""} onChange={(e) => updateConfig("members", "memberNumberFormat", e.target.value)} placeholder='M{year}{number}' className="h-9 text-sm" />
            <p className="text-xs text-gray-400">Use {"{year}"} and {"{number}"} as placeholders.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-5">
      {/* Notification Channels */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-base">Notification Channels</CardTitle>
          </div>
          <CardDescription>Enable communication channels for member notifications.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-1">
            <SettingsToggle section="notifications" configKey="smsNotifications" title="SMS Notifications" description="Send notifications via SMS to member phone numbers." />
            <SettingsToggle section="notifications" configKey="emailNotifications" title="Email Notifications" description="Send notifications via email to member addresses." />
          </div>
        </CardContent>
      </Card>

      {/* Notification Events */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base">Notification Events</CardTitle>
          </div>
          <CardDescription>Choose which events trigger notifications to members.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-1">
            <SettingsToggle section="notifications" configKey="contributionReminders" title="Contribution Reminders" description="Remind members of upcoming or missed contributions." />
            <SettingsToggle section="notifications" configKey="loanDueReminders" title="Loan Due Reminders" description="Remind members of upcoming or overdue loan payments." />
            <SettingsToggle section="notifications" configKey="loanApprovalNotifications" title="Loan Approval Notifications" description="Notify members when their loan is approved or rejected." />
            <SettingsToggle section="notifications" configKey="paymentConfirmationNotifications" title="Payment Confirmation Notifications" description="Send confirmation when a payment is received." />
          </div>
          <Separator className="my-3" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Reminder Days Before Due</Label>
              <Input type="number" value={config.notifications?.reminderDaysBeforeDue ?? 3} onChange={(e) => updateConfig("notifications", "reminderDaysBeforeDue", Number(e.target.value))} className="h-9 text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Reminder Frequency</Label>
              <Select value={config.notifications?.reminderFrequency || "daily"} onValueChange={(v) => updateConfig("notifications", "reminderFrequency", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["daily", "weekly", "monthly"].map((f) => (
                    <SelectItem key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-5">
      {/* Authentication */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-600" />
            <CardTitle className="text-base">Authentication</CardTitle>
          </div>
          <CardDescription>Configure authentication policies and security measures.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Password Expiry (Days)</Label>
              <Input type="number" value={config.security?.passwordExpiryDays ?? 90} onChange={(e) => updateConfig("security", "passwordExpiryDays", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Days after which password must be changed.</p>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="space-y-1">
            <SettingsToggle section="security" configKey="forcePasswordChange" title="Force Password Change on First Login" description="Require new users to change password on initial login." />
            <SettingsToggle section="security" configKey="twoFactorAuthentication" title="Two-Factor Authentication" description="Require 2FA for all user logins." />
          </div>
        </CardContent>
      </Card>

      {/* Session Controls */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-base">Session Controls</CardTitle>
          </div>
          <CardDescription>Configure session timeout and login attempt limits.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Session Timeout (Minutes)</Label>
              <Input type="number" value={config.security?.sessionTimeout ?? 30} onChange={(e) => updateConfig("security", "sessionTimeout", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Auto-logout after inactivity.</p>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Login Attempts Limit</Label>
              <Input type="number" value={config.security?.loginAttemptsLimit ?? 5} onChange={(e) => updateConfig("security", "loginAttemptsLimit", Number(e.target.value))} className="h-9 text-sm" />
              <p className="text-xs text-gray-400">Max failed attempts before account lockout.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderReports = () => (
    <div className="space-y-5">
      {/* Report Branding */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileBarChart className="h-5 w-5 text-cyan-600" />
            <CardTitle className="text-base">Report Branding</CardTitle>
          </div>
          <CardDescription>Configure how reports are branded and presented.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-1">
            <SettingsToggle section="reports" configKey="cooperativeLogoOnReports" title="Show Cooperative Logo on Reports" description="Display the cooperative logo in report headers." />
            <SettingsToggle section="reports" configKey="signatureOnReports" title="Show Signature on Reports" description="Display a signature or seal on report exports." />
          </div>
          <Separator className="my-3" />
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700">Watermark Text</Label>
            <Input value={config.reports?.watermark || ""} onChange={(e) => updateConfig("reports", "watermark", e.target.value)} placeholder="CONFIDENTIAL" className="h-9 text-sm" />
            <p className="text-xs text-gray-400">Watermark text for sensitive reports (leave empty for none).</p>
          </div>
        </CardContent>
      </Card>

      {/* Export Preferences */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-base">Export Preferences</CardTitle>
          </div>
          <CardDescription>Configure default report export options.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-gray-700">Default Report Format</Label>
              <Select value={config.reports?.defaultReportFormat || "PDF"} onValueChange={(v) => updateConfig("reports", "defaultReportFormat", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["PDF", "Excel", "CSV", "HTML"].map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700">Report Footer Text</Label>
            <Textarea value={config.reports?.reportFooterText || ""} onChange={(e) => updateConfig("reports", "reportFooterText", e.target.value)} placeholder="Footer text for all reports..." className="min-h-[60px] text-sm" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-5">
      {/* User Management */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-slate-600" />
            <CardTitle className="text-base">Users & Roles Management</CardTitle>
          </div>
          <CardDescription>Manage system users, roles, and permissions from dedicated pages.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 text-center">
          <UserCog className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-600 mb-1">Manage users and roles from the dedicated pages.</p>
          <p className="text-xs text-gray-400 mb-4">Configure roles, permissions, and system users.</p>
          <div className="flex items-center justify-center gap-3">
            <Button size="sm" variant="outline" onClick={() => navigate("/settings/users-roles")}>
              <Users className="h-4 w-4 mr-1.5" />
              Manage Users & Roles
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Role Hierarchy */}
      <Card className="border rounded-md shadow-none">
        <CardHeader className="pb-3 border-b bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-base">Role Hierarchy</CardTitle>
          </div>
          <CardDescription>Predefined roles and their access levels within the system.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-2">
            {["System Admin", "President", "Vice President", "Secretary", "Accountant", "Loan Officer", "Auditor", "Member"].map((role) => (
              <div key={role} className="flex items-center gap-3 px-3 py-2 rounded-md bg-gray-50 border text-sm">
                <Badge variant="outline" className="bg-white font-normal text-xs">{role}</Badge>
                <span className="text-xs text-gray-400">Full access to relevant modules</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderPaymentMethods = () => (
    <PaymentMethods embedded={true} />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "organization": return renderOrganization();
      case "savings": return renderSavings();
      case "loanProducts": return renderLoanProducts();
      case "loanPolicies": return renderLoanPolicies();
      case "penalties": return renderPenalties();
      case "accounting": return renderAccounting();
      case "paymentMethods": return renderPaymentMethods();
      case "payroll": return renderPayroll();
      case "members": return renderMembers();
      case "notifications": return renderNotifications();
      case "security": return renderSecurity();
      case "reports": return renderReports();
      case "users": return renderUsers();
      default: return renderOrganization();
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-6">
      {/* Hero Header Card */}
      <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden rounded-[14px]">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex items-center gap-2">
                <SettingsIcon className="h-6 w-6 text-white/90" />
                System Settings
              </h1>
              <p className="mt-1.5 text-sm sm:text-base text-white/80 max-w-xl leading-relaxed">
                Configure your cooperative organization, savings, loans, and system preferences
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2 sm:mt-0">
              {isDirty && (
                <span className="flex items-center gap-1.5 text-amber-300 text-xs bg-white/10 px-3 py-1.5 rounded-lg">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Unsaved changes
                </span>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="bg-white text-[#009A44] hover:bg-white/90 font-semibold h-9 rounded-lg min-w-[100px]"
              >
                {saving ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-3.5 w-3.5 mr-1.5" /> Save Settings</>
                )}
              </Button>
              {isDirty && (
                <Button variant="outline" size="sm" onClick={loadSettings} className="bg-white/15 text-white hover:bg-white/25 border border-white/20 h-9 rounded-lg">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Reset
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className={`transition-all duration-300 ease-in-out ${sidebarCollapsed ? "lg:w-14" : "lg:w-56"} shrink-0`}>
          <div className="lg:sticky lg:top-6 space-y-1">
            <div className={`flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} mb-2 px-1`}>
              {!sidebarCollapsed && (
                <div>
                  <h2 className="text-sm font-semibold text-slate-700">Settings Sections</h2>
                  <p className="text-[11px] text-slate-400 mt-0.5">Configure your cooperative</p>
                </div>
              )}
              <button
                onClick={toggleSidebar}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
            </div>
            <nav className="flex lg:flex-col gap-0.5 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActiveTab = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => tab.id === "users" ? navigate("/settings/users-roles") : setActiveTab(tab.id)}
                    title={sidebarCollapsed ? tab.label : undefined}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      isActiveTab
                        ? "bg-[#0A9F43] text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-100"
                    } ${sidebarCollapsed ? "justify-center mx-auto w-10 h-10" : ""}`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActiveTab ? "text-white" : "text-slate-400"}`} />
                    <span className={`transition-opacity duration-200 ${sidebarCollapsed ? "hidden" : ""}`}>
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px]">
            <CardContent className="p-4 sm:p-6">
              {renderTabContent()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
